/**
 * Fixes — proposed source-code fixes for a completed scan (ADR-0006, Phase 2b).
 *
 * The engine pins each fixable issue to its element and proposes the concrete
 * source change it needs — a real change to your code/CMS, never a runtime
 * overlay. This page surfaces those proposals for review. Applying them (repo
 * PR / CMS change) is a later phase; issues we can't fix at the source stay
 * openly disclosed in your report.
 */

import { useEffect, useState } from 'preact/hooks';
import {
  type CreateFixPrResponse,
  DashboardApi,
  type FixDescriptorView,
  type FixPrStatusResponse,
  type FixPreviewResponse,
  type FixVerificationResponse,
  type ScanFixesResponse,
} from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

interface ScanOption {
  id: string;
  productName: string;
  completedAt: string | null;
}

const SOURCE_LABEL: Record<FixDescriptorView['source'], string> = {
  deterministic: 'Ready',
  generated: 'Needs review',
  'needs-text': 'Needs your input',
};
const SOURCE_PILL: Record<FixDescriptorView['source'], string> = {
  deterministic: 'pill-supports',
  generated: 'pill-info',
  'needs-text': 'pill-muted',
};

type OpenPrState = Exclude<FixPrStatusResponse['status'], 'none'>;
const PR_STATUS_LABEL: Record<OpenPrState, string> = {
  open: 'open',
  merged: 'merged',
  closed: 'closed',
};
const PR_STATUS_PILL: Record<OpenPrState, string> = {
  open: 'pill-info',
  merged: 'pill-supports',
  closed: 'pill-muted',
};

type OutcomeStatus = FixVerificationResponse['outcomes'][number]['status'];
const VERIFY_LABEL: Record<OutcomeStatus, string> = {
  resolved: '✓ resolved',
  unresolved: 'not confirmed',
  regressed: '⚠ regressed',
};
const VERIFY_PILL: Record<OutcomeStatus, string> = {
  resolved: 'pill-supports',
  unresolved: 'pill-muted',
  regressed: 'pill-info',
};

const changeSummary = (c: FixDescriptorView['change']): string => {
  switch (c.kind) {
    case 'set-attribute':
      return c.value === null || c.value === undefined
        ? `set ${c.name}="…"`
        : `set ${c.name}="${c.value}"`;
    case 'set-text':
      return c.value == null ? 'set link text "…"' : `set text "${c.value}"`;
    case 'set-lang':
      return c.value == null ? 'set page language "…"' : `set lang="${c.value}"`;
    case 'set-style':
      return `set ${c.property}: ${c.value}`;
    case 'set-heading-level':
      return `use heading level ${c.level}`;
    default:
      return c.note ?? 'manual fix required';
  }
};

const shortSelector = (s: string): string => {
  const parts = s.split('>').map((p) => p.trim());
  return parts.length <= 2 ? s : `… > ${parts.slice(-2).join(' > ')}`;
};

/** Per-line color for a unified-diff line (file headers first, then hunks). */
const diffLineStyle = (line: string): Record<string, string> => {
  if (line.startsWith('+++') || line.startsWith('---')) return { color: 'var(--muted, #6b7280)' };
  if (line.startsWith('@@')) return { color: '#8250df' };
  if (line.startsWith('+')) return { color: '#116329', background: 'rgba(46,160,67,0.12)' };
  if (line.startsWith('-')) return { color: '#a40e26', background: 'rgba(248,81,73,0.12)' };
  return {};
};

const renderDiff = (diff: string): preact.ComponentChildren =>
  diff.split('\n').map((line, i) => (
    <div key={`${i}:${line}`} style={{ ...diffLineStyle(line), whiteSpace: 'pre' }}>
      {line === '' ? ' ' : line}
    </div>
  ));

export function Remediations({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [scans, setScans] = useState<ScanOption[] | null>(null);
  const [scanId, setScanId] = useState<string>('');
  const [data, setData] = useState<ScanFixesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [prBusy, setPrBusy] = useState(false);
  const [prResult, setPrResult] = useState<CreateFixPrResponse | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [preview, setPreview] = useState<FixPreviewResponse | null>(null);
  const [prStatus, setPrStatus] = useState<FixPrStatusResponse | null>(null);
  const [verifyAgainst, setVerifyAgainst] = useState<string>('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verification, setVerification] = useState<FixVerificationResponse | null>(null);

  const runVerify = async () => {
    if (!scanId || !verifyAgainst) return;
    setVerifyBusy(true);
    setVerification(null);
    setError(null);
    try {
      setVerification(await api.verifyFixes(org.id, scanId, verifyAgainst));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setVerifyBusy(false);
    }
  };

  // Best-effort: a repo may not be connected (400) — stay silent, just no chip.
  const refreshPrStatus = (id: string) => {
    api
      .fixPrStatus(org.id, id)
      .then(setPrStatus)
      .catch(() => setPrStatus(null));
  };

  const runPreview = async () => {
    if (!scanId) return;
    setPreviewBusy(true);
    setPreview(null);
    setError(null);
    try {
      setPreview(await api.previewFixPr(org.id, scanId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewBusy(false);
    }
  };

  const createPr = async () => {
    if (!scanId) return;
    setPrBusy(true);
    setPrResult(null);
    setError(null);
    try {
      setPrResult(await api.createFixPr(org.id, scanId));
      refreshPrStatus(scanId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPrBusy(false);
    }
  };

  const copyInstructions = async () => {
    if (!data?.agentTask) return;
    try {
      await navigator.clipboard.writeText(data.agentTask);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to the clipboard.');
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.listScans(org.id);
        const completed = r.scans
          .filter((s) => s.status === 'completed')
          .map((s) => ({ id: s.id, productName: s.productName, completedAt: s.completedAt }));
        setScans(completed);
        if (completed[0]) setScanId(completed[0].id);
      } catch (e) {
        setError(String(e));
        setScans([]);
      }
    })();
  }, [org.id]);

  useEffect(() => {
    if (!scanId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    setPrResult(null);
    setPreview(null);
    setPrStatus(null);
    setVerifyAgainst('');
    setVerification(null);
    api
      .fixPrStatus(org.id, scanId)
      .then(setPrStatus)
      .catch(() => setPrStatus(null));
    api
      .scanFixes(org.id, scanId)
      .then((r) => setData(r))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [org.id, scanId]);

  return (
    <div>
      <div class="page-header">
        <h1>Fixes</h1>
        <p class="muted">
          Proposed source-code fixes, each pinned to the exact element the scan flagged. These are
          real changes to your code or CMS — never a runtime overlay. Applying them lands in a later
          release; anything we can't fix at the source stays openly disclosed in your report.
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      <div class="card" style={{ marginBottom: 16 }}>
        <div class="field">
          <label htmlFor="fx-scan">Completed scan</label>
          <select
            id="fx-scan"
            value={scanId}
            onChange={(e) => setScanId((e.target as HTMLSelectElement).value)}
          >
            {scans?.length === 0 && <option value="">No completed scans yet</option>}
            {scans?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.productName} — {s.id.slice(0, 8)}
                {s.completedAt ? ` (${new Date(s.completedAt).toLocaleDateString()})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <p class="muted">
          <output>Generating proposed fixes…</output>
        </p>
      )}

      {!loading && data && data.summary.total === 0 && (
        <div class="empty-state" style={{ padding: '24px 8px' }}>
          <p>
            No auto-proposable fixes for this scan. The engine currently proposes fixes for
            element-pinned issues (missing image text, control names, page language); everything
            else stays disclosed in the report until fixed at the source.
          </p>
        </div>
      )}

      {!loading && data && data.summary.total > 0 && (
        <>
          <div
            class="card"
            style={{
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <p class="muted small" style={{ margin: 0 }}>
              <strong>{data.summary.total}</strong> proposed{' '}
              {data.summary.total === 1 ? 'fix' : 'fixes'} · {data.summary.deterministic} ready ·{' '}
              {data.summary.generated} to review · {data.summary.needsText} need your input
            </p>
            {data.summary.deliverable > 0 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => void copyInstructions()}>
                  {copied ? 'Copied ✓' : `Copy fix instructions (${data.summary.deliverable})`}
                </button>
                <button
                  type="button"
                  class="secondary"
                  onClick={() => void runPreview()}
                  disabled={previewBusy || prBusy}
                >
                  {previewBusy ? 'Previewing…' : 'Preview changes'}
                </button>
                <button
                  type="button"
                  onClick={() => void createPr()}
                  disabled={prBusy || previewBusy}
                >
                  {prBusy ? 'Opening PR…' : 'Create PR'}
                </button>
              </div>
            )}
          </div>
          {prStatus && prStatus.status !== 'none' && prStatus.pr && (
            <p class="muted small" style={{ margin: '0 0 12px' }}>
              Fix PR:{' '}
              <a href={prStatus.pr.url} target="_blank" rel="noopener noreferrer">
                #{prStatus.pr.number}
              </a>{' '}
              <span class={`pill ${PR_STATUS_PILL[prStatus.status]}`}>
                {PR_STATUS_LABEL[prStatus.status]}
              </span>
            </p>
          )}
          {prResult?.status === 'opened' && prResult.pr && (
            <Banner variant="info">
              Opened{' '}
              <a href={prResult.pr.url} target="_blank" rel="noopener noreferrer">
                PR #{prResult.pr.number}
              </a>{' '}
              with {prResult.changedFiles ?? 0} changed file
              {prResult.changedFiles === 1 ? '' : 's'} — review and merge, then re-scan to verify.
            </Banner>
          )}
          {prResult?.status === 'skipped' && (
            <Banner variant="warn">
              No PR opened
              {prResult.reason === 'no-source-changes'
                ? ' — the agent could not locate these elements in the connected repo’s source.'
                : ' — no fixes were ready to deliver.'}
            </Banner>
          )}
          {preview?.status === 'previewed' && preview.files && preview.files.length > 0 && (
            <div class="card" style={{ marginBottom: 16 }}>
              <p class="muted small" style={{ marginTop: 0 }}>
                Preview — {preview.files.length} file{preview.files.length === 1 ? '' : 's'} would
                change across {preview.fixes ?? 0} fix{(preview.fixes ?? 0) === 1 ? '' : 'es'}.
                Nothing has been applied; this is exactly what the PR would contain.
              </p>
              {preview.files.map((file) => (
                <details key={file.path} open style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: 'pointer' }}>
                    <code>{file.path}</code>{' '}
                    <span class="muted small">
                      +{file.added} −{file.removed}
                    </span>
                  </summary>
                  <pre
                    style={{
                      overflowX: 'auto',
                      margin: '8px 0 0',
                      padding: 12,
                      borderRadius: 6,
                      fontSize: 12,
                      lineHeight: 1.5,
                      background: 'var(--code-bg, #f6f8fa)',
                    }}
                  >
                    {renderDiff(file.diff)}
                  </pre>
                </details>
              ))}
            </div>
          )}
          {preview?.status === 'skipped' && (
            <Banner variant="warn">
              Nothing to preview
              {preview.reason === 'no-source-changes'
                ? ' — the agent could not locate these elements in the connected repo’s source.'
                : ' — no fixes were ready to deliver.'}
            </Banner>
          )}
          <table class="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Criterion</th>
                <th>Element</th>
                <th>Proposed change</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.fixes.map((f) => (
                <tr key={`${f.criterion}:${f.selector}`}>
                  <td>{f.criterion}</td>
                  <td>
                    <code title={f.selector}>{shortSelector(f.selector)}</code>
                  </td>
                  <td>
                    {changeSummary(f.change)}
                    <div class="muted small">{f.rationale}</div>
                  </td>
                  <td>
                    <span class={`pill ${SOURCE_PILL[f.source]}`}>{SOURCE_LABEL[f.source]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {scans && scans.length > 1 && (
            <div class="card" style={{ marginTop: 16 }}>
              <p class="muted small" style={{ marginTop: 0 }}>
                Verify fixes landed — pick a later re-scan of this product to check which of these
                proposed fixes it actually resolved. A fix is marked resolved only when the re-scan
                reports that criterion as fully supported; anything unconfirmed (still failing,
                partial, or not re-evaluated) stays openly disclosed. Nothing is claimed from the
                proposal alone.
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  aria-label="Re-scan to verify against"
                  value={verifyAgainst}
                  onChange={(e) => setVerifyAgainst((e.target as HTMLSelectElement).value)}
                >
                  <option value="">Select a re-scan…</option>
                  {scans
                    .filter((s) => s.id !== scanId)
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.productName} — {s.id.slice(0, 8)}
                        {s.completedAt ? ` (${new Date(s.completedAt).toLocaleDateString()})` : ''}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  disabled={!verifyAgainst || verifyBusy}
                  onClick={() => void runVerify()}
                >
                  {verifyBusy ? 'Verifying…' : 'Verify fixes'}
                </button>
              </div>
              {verification && (
                <div style={{ marginTop: 12 }}>
                  <p class="small" style={{ margin: '0 0 8px' }}>
                    <strong>{verification.resolved}</strong> of {verification.proposed} proposed{' '}
                    {verification.proposed === 1 ? 'fix' : 'fixes'} resolved by the re-scan
                    {verification.regressed > 0 ? ` · ${verification.regressed} regressed` : ''}
                    {verification.unresolved > 0
                      ? ` · ${verification.unresolved} not confirmed`
                      : ''}
                    .
                  </p>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {verification.outcomes.map((o) => (
                      <span key={o.criterion} class={`pill ${VERIFY_PILL[o.status]}`}>
                        {o.criterion} {VERIFY_LABEL[o.status]}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
